import React, { useEffect, useRef } from 'react';

/**
 * The five WebGL backgrounds (handoff §5.6). The GLSL prelude and the fragment programs are
 * the prototype's, verbatim. Each rule in the component was learned from a real bug:
 *
 *  - render at 0.6× the CSS size; uniforms are `r` (resolution) and `t` (seconds + 12);
 *  - the caller keys the component by background kind so every switch gets a fresh canvas
 *    (a canvas reused after WEBGL_lose_context stays black);
 *  - wait for a non-zero client size before the first draw;
 *  - handle webglcontextlost; on unmount cancel the rAF and lose the context;
 *  - the swatch gradient sits blurred underneath and the canvas fades in after its first
 *    frame, so it never shows black;
 *  - under reduced motion draw one frame, no rAF;
 *  - off the screen it stops drawing (an IntersectionObserver on the wrapper; the landing page
 *    runs one full-screen and it was still rendering 60 fps under the FAQ) — the rAF keeps
 *    ticking so the first visible frame is immediate;
 *  - compile and link failures are logged, never swallowed.
 *
 * Budget (Oct 2026 — the lasers program is the page background on every inside route, so it
 * runs for as long as the app is open):
 *
 *  - 30 fps, not the display's rate: the slowest-moving term in any program drifts far too
 *    slowly for 60 (or 120 on ProMotion) to show, and skipping alternate ticks halves the GPU
 *    and compositor work on every device;
 *  - at most `maxPixels` (1.2M) per frame: a 4K or ultrawide at 1× would otherwise cost four
 *    1080p screens, and none of these programs reward the detail;
 *  - a software renderer (SwiftShader, llvmpipe — WebGL that is really the CPU) gets one still
 *    frame, like reduced motion; the gradient drifts are not worth a pegged core;
 *  - adaptive: the first seconds of ticks are sampled and if the browser can't sustain
 *    ~25 fps while the canvas is on screen the shader freezes on its last frame. A median over
 *    the window, with tab switches thrown out, so a hiccup on load doesn't decide it;
 *  - `preserveDrawingBuffer` only when the canvas will be read back (`capture`) — a Portal card
 *    is filmed for the share sheet, the page background and the landing hero never are.
 */
export const GLSL = 'precision highp float;uniform vec2 r;uniform float t;'
  + 'vec3 ring(float x){x=fract(x)*6.0;vec3 c0=vec3(.99,.10,.62),c1=vec3(.71,.69,1.),c2=vec3(.07,.48,.99),c3=vec3(.13,.93,.98),c4=vec3(.98,.88,.71),c5=vec3(.99,.67,.43);'
  + 'vec3 c=mix(c0,c1,clamp(x,0.,1.));c=mix(c,c2,clamp(x-1.,0.,1.));c=mix(c,c3,clamp(x-2.,0.,1.));c=mix(c,c4,clamp(x-3.,0.,1.));c=mix(c,c5,clamp(x-4.,0.,1.));return mix(c,c0,clamp(x-5.,0.,1.));}'
  + 'float h1(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}'
  + 'float n2(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(h1(i),h1(i+vec2(1,0)),f.x),mix(h1(i+vec2(0,1)),h1(i+vec2(1,1)),f.x),f.y);}'
  + 'float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<5;i++){v+=a*n2(p);p=p*2.03+vec2(1.7,9.2);a*=.5;}return v;}';

export const FRAGS = {
  fractal: 'void main(){vec2 uv=(gl_FragCoord.xy-.5*r)/r.y;vec2 p=uv*1.5;float a=t*.04;vec3 col=vec3(0.);mat2 m=mat2(cos(a),-sin(a),sin(a),cos(a));'
    + 'for(int i=0;i<8;i++){p=abs(p)/dot(p,p)-vec2(.92+.1*sin(t*.13),.58+.08*cos(t*.11));p=m*p;float d=length(p);float fil=exp(-abs(d-1.)*6.);col+=ring(d*.3+float(i)*.11+t*.03)*fil*.22;}'
    + 'col=pow(col,vec3(1.8))*2.2;col=col/(1.+col*.35);col*=smoothstep(1.25,.15,length(uv));gl_FragColor=vec4(col,1.);}',
  liquid: 'void main(){vec2 uv=gl_FragCoord.xy/r.y;vec2 q=vec2(fbm(uv*1.8+t*.04),fbm(uv*1.8+vec2(5.2,1.3)-t*.03));'
    + 'vec2 w=vec2(fbm(uv*1.8+4.*q+vec2(1.7,9.2)+t*.07),fbm(uv*1.8+4.*q+vec2(8.3,2.8)-t*.05));float v=fbm(uv*1.8+4.*w);'
    + 'vec3 col=ring(v*1.6+length(w)*.7+t*.015);float k=smoothstep(.22,.72,v);col=mix(vec3(.015,0.,.035),col,k);col+=ring(v+.3)*pow(k,4.)*.35;col*=.95;gl_FragColor=vec4(col,1.);}',
  mirror: 'void main(){vec2 uv=(gl_FragCoord.xy-.5*r)/r.y;vec2 c=uv-vec2(0.,.62);float ang=atan(c.y,c.x),rad=length(c);vec3 col=vec3(.012,0.,.03)+ring(ang*.15+t*.02)*.05;'
    + 'for(int L=0;L<3;L++){float fl=float(L);vec2 g=vec2(ang*(7.+fl*3.)+t*(.12+fl*.05),rad*(10.+fl*4.));vec2 id=floor(g),fp=fract(g)-.5;float rn=h1(id+fl*7.3);'
    + 'float s=smoothstep(.14,.02,max(abs(fp.x),abs(fp.y))-.06*rn)*step(.6,rn);float tw=.4+.6*sin(t*2.5+rn*50.);col+=mix(vec3(1.),ring(rn+t*.03),.8)*s*tw*(1.1-rad*.5)*(1.-fl*.25);}'
    + 'col+=vec3(1.,.85,.95)*.35*exp(-rad*8.);col+=ring(t*.05)*.18*exp(-rad*2.5);gl_FragColor=vec4(col,1.);}',
  lasers: 'void main(){vec2 p=(gl_FragCoord.xy-vec2(.5*r.x,-.04*r.y))/r.y;float ang=atan(p.x,p.y),d=length(p);vec3 col=vec3(.02,0.,.035);'
    + 'float hz=fbm(p*2.5+vec2(t*.1,-t*.05));for(int i=0;i<10;i++){float fi=float(i);float a=(fi-4.5)*.15+.32*sin(t*.5+fi*.9);float w=abs(ang-a);'
    + 'col+=ring(fi*.1+t*.04)*(.0022/(w+.0018))*smoothstep(1.9,.1,d)*(.45+.9*hz)*.5;}'
    + 'col+=ring(t*.06)*exp(-d*4.)*.45;col+=vec3(.2,.04,.25)*hz*.25;col=col/(1.+col*.25);gl_FragColor=vec4(col,1.);}',
  // TUNNEL was nine DOM rings, each a near-card-sized layer animated from scale .04 to 1.9.
  // Chromium rasters a compositor-animated layer at the animation's LARGEST scale, so one card
  // asked for nine ~2000px textures at once — fine on a desktop GPU, far past the Android
  // WebView's tile budget, where the evicted tiles came back as the ring's edge pixels smeared
  // across the whole screen. Same picture, one canvas: Chebyshev distance makes the squares.
  tunnel: 'void main(){vec2 uv=(gl_FragCoord.xy-vec2(.5*r.x,.58*r.y))/r.y;float d=max(abs(uv.x),abs(uv.y));'
    + 'vec3 col=vec3(.012,.008,.03)+vec3(.71,.69,1.)*.10*smoothstep(.55,0.,length(uv));'
    + 'float lw=.9/r.y,gw=5.5/r.y;'
    + 'for(int i=0;i<9;i++){float p=fract(t/9.+float(i)/9.);float e=abs(d-mix(.012,.47,p));'
    + 'col+=ring(float(i)/7.)*min(p/.15,1.15-p)*(smoothstep(lw,0.,e)+exp(-e/gw)*.18);}'
    + 'col=col/(1.+col*.18);gl_FragColor=vec4(col,1.);}',
  warp: 'void main(){vec2 p=(gl_FragCoord.xy-.5*r)/r.y;p*=mat2(cos(t*.1),-sin(t*.1),sin(t*.1),cos(t*.1));float a=atan(p.y,p.x)/3.14159,rr=length(p);'
    + 'vec2 tu=vec2(a*3.+.4*sin(.3/rr+t),.35/rr+t*.45);float s=sin(tu.x*6.2832+sin(tu.y*6.)*1.4)*sin(tu.y*9.);'
    + 'vec3 col=ring(tu.y*.25+a*.5)*smoothstep(-.2,1.,s);col+=ring(tu.y*.25+.5)*pow(max(0.,-s),3.)*.6;col*=smoothstep(.0,.45,rr);col+=vec3(1.,.9,1.)*exp(-rr*18.)*.6;gl_FragColor=vec4(col*.9,1.);}',
};

const VERT = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}';

function reducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
}

/** Default budget. `fps` is a cap, `maxPixels` the most the canvas may hold, `slowMs` the median
 * tick gap that freezes the shader, `sampleAfterMs` / `sampleForMs` the window it is measured in. */
export const BUDGET = Object.freeze({ scale: 0.6, fps: 30, maxPixels: 1_200_000, slowMs: 40, sampleAfterMs: 1000, sampleForMs: 2000, minSamples: 20 });

const SOFTWARE_GPU = /swiftshader|llvmpipe|softpipe|software|microsoft basic render|mesa offscreen/i;

/** True when WebGL is being rendered on the CPU. Asks the debug extension first (Chromium
 * answers the real driver string there); Firefox puts it on RENDERER when it says at all. */
export function isSoftwareRenderer(gl) {
  try {
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    const name = (dbg && gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) || gl.getParameter(gl.RENDERER) || '';
    return SOFTWARE_GPU.test(String(name));
  } catch { return false; }
}

/** Canvas pixels for a CSS box: `scale` of it, shrunk further so the area stays under `maxPixels`. */
export function canvasSize(cw, ch, { scale = BUDGET.scale, maxPixels = BUDGET.maxPixels } = {}) {
  const k = Math.min(scale, Math.sqrt(maxPixels / Math.max(1, cw * ch)));
  return { w: Math.max(1, Math.round(cw * k)), h: Math.max(1, Math.round(ch * k)) };
}

export function median(xs) {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/** Compile, link and run one fragment program on a canvas. Returns a stop function. */
export function runShader(cv, frag, {
  onFirstFrame = null, still = reducedMotion(), log = console, visible = () => true,
  capture = true, budget = BUDGET, onDegrade = null, now = () => performance.now(),
} = {}) {
  let gl = null;
  try {
    // preserveDrawingBuffer only for a canvas that is read back (the share sheet draws a Portal
    // card's shader into its own canvas); without it the buffer is cleared after every frame and
    // the capture is black. Everywhere else it is an extra copy per frame, worst on phones.
    gl = cv.getContext('webgl', { antialias: false, alpha: true, premultipliedAlpha: false, powerPreference: 'low-power', preserveDrawingBuffer: !!capture })
      || cv.getContext('experimental-webgl');
  } catch { gl = null; }
  if (!gl) { log.warn('Portal shader: WebGL unavailable, showing fallback'); return () => {}; }
  if (!still && isSoftwareRenderer(gl)) {
    log.info?.('Portal shader: software renderer, drawing one frame');
    still = true;
    onDegrade?.('software');
  }

  let dead = false;
  let raf = 0;
  const onLost = (e) => { e.preventDefault(); dead = true; cancelAnimationFrame(raf); cv.style.opacity = 0; };
  cv.addEventListener('webglcontextlost', onLost);

  const mk = (type, src) => {
    const o = gl.createShader(type);
    gl.shaderSource(o, src);
    gl.compileShader(o);
    if (!gl.getShaderParameter(o, gl.COMPILE_STATUS)) log.warn(`Portal shader compile failed: ${gl.getShaderInfoLog(o) || 'unknown'}`);
    return o;
  };
  const pr = gl.createProgram();
  gl.attachShader(pr, mk(gl.VERTEX_SHADER, VERT));
  gl.attachShader(pr, mk(gl.FRAGMENT_SHADER, GLSL + frag));
  gl.linkProgram(pr);
  if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) {
    log.warn(`Portal shader link failed: ${gl.getProgramInfoLog(pr) || 'unknown'}`);
    cv.removeEventListener('webglcontextlost', onLost);
    return () => {};
  }
  gl.useProgram(pr);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(pr, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  const ur = gl.getUniformLocation(pr, 'r');
  const ut = gl.getUniformLocation(pr, 't');
  const t0 = now();
  const interval = 1000 / budget.fps;
  let shown = false;
  let shownAt = null;
  let lastDraw = -Infinity;
  let lastTick = 0;
  let gaps = [];
  let judged = false;

  const paint = () => {
    const { w, h } = canvasSize(cv.clientWidth, cv.clientHeight, budget);
    if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
    gl.viewport(0, 0, w, h);
    gl.uniform2f(ur, w, h);
    gl.uniform1f(ut, (now() - t0) / 1000 + 12);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    if (!shown) { shown = true; shownAt = now(); cv.style.opacity = 1; onFirstFrame?.(); }
  };

  // Is the browser keeping up? Tick gaps while the canvas is on screen, from a second after the
  // first frame (load is busy on any machine) for two more. Gaps over 250 ms are a hidden tab or
  // a paused page, not slowness, and are dropped; the median decides, so one stall doesn't.
  const sample = (t) => {
    if (judged || shownAt == null || t - shownAt < budget.sampleAfterMs) { lastTick = t; return; }
    const gap = t - lastTick;
    lastTick = t;
    if (gap > 0 && gap < 250 && !(typeof document !== 'undefined' && document.hidden)) gaps.push(gap);
    if (t - shownAt < budget.sampleAfterMs + budget.sampleForMs) return;
    judged = true;
    const m = median(gaps);
    if (gaps.length >= budget.minSamples && m > budget.slowMs) {
      log.info?.(`Portal shader: ${Math.round(1000 / m)} fps, freezing on this frame`);
      still = true;
      onDegrade?.('slow');
    }
    gaps = [];
  };

  const draw = () => {
    if (dead) return;
    if (!cv.clientWidth || !cv.clientHeight || !visible()) { raf = requestAnimationFrame(draw); return; }
    const t = now();
    if (shown) { sample(t); if (still) return; }    // judged slow mid-loop: the last frame stays up
    // Alternate ticks at 60 Hz, every fourth at 120: a 2 ms allowance keeps rAF jitter from
    // turning the cap into 20 fps.
    if (t - lastDraw >= interval - 2) { lastDraw = t; paint(); }
    if (!still) raf = requestAnimationFrame(draw);
  };
  draw();

  // A still frame is lost when the box changes size (the canvas is cleared on resize), so draw it
  // once more. Animated, the loop repaints on its own.
  const onResize = () => { if (still && !dead && cv.clientWidth && cv.clientHeight) paint(); };
  window.addEventListener('resize', onResize);

  return () => {
    dead = true;
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', onResize);
    cv.removeEventListener('webglcontextlost', onLost);
    const x = gl.getExtension('WEBGL_lose_context');
    if (x) x.loseContext();
  };
}

/**
 * One shader background. Mount it with `key={kind}`. `fallback` is the swatch gradient that
 * shows, blurred, under the canvas — and is all that shows where WebGL is missing. `capture`
 * keeps the drawing buffer so the canvas can be read back (a Portal card the share sheet
 * films); pass false for a background nothing ever reads.
 */
export default function PortalShader({ frag, fallback = '#000', capture = true }) {
  const boxRef = useRef(null);
  useEffect(() => {
    const box = boxRef.current;
    if (!box || !frag) return undefined;
    // jsdom and old engines: no WebGL at all, and no point asking a canvas for it.
    if (typeof window === 'undefined' || typeof window.WebGLRenderingContext === 'undefined') return undefined;
    // The canvas is made here, not in JSX: an effect that re-runs on the same node (React's
    // dev double-mount, a hot reload) would otherwise draw on a context the last run lost.
    const cv = document.createElement('canvas');
    cv.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;opacity:0;transition:opacity .3s';
    box.appendChild(cv);
    let onScreen = true;
    const io = typeof IntersectionObserver === 'undefined' ? null
      : new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; }, { rootMargin: '10% 0px' });
    io?.observe(box);
    const stop = runShader(cv, frag, { visible: () => onScreen, capture });
    return () => { io?.disconnect(); stop(); if (cv.parentNode === box) box.removeChild(cv); };
  }, [frag, capture]);
  return (
    <div aria-hidden data-testid="portal-shader" style={{ position: 'absolute', inset: 0, background: '#000', overflow: 'hidden', pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', inset: '-20%', background: fallback, filter: 'blur(40px) saturate(1.2)', opacity: 0.55 }} />
      <div ref={boxRef} style={{ position: 'absolute', inset: 0 }} />
      <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(0deg, rgba(255,255,255,.03) 0 1px, transparent 1px 3px)' }} />
    </div>
  );
}
